import { Site } from '@/types/assets';

/**
 * Determines if a site is international (non-US).
 * A site is considered international if it has an address with a country code
 * that is not 'US' or 'USA'.
 *
 * @param site - The site to check
 * @returns true if the site is international, false otherwise
 */
export const isInternationalSite = (site: Site): boolean => {
  if (!site.address?.country) {
    // If no country is specified, assume US (default)
    return false;
  }

  const country = site.address.country.toUpperCase();
  return country !== 'US' && country !== 'USA';
};

const CA_PR_COUNTRY_CODES = ['CA', 'PR'];

/**
 * Determines if a site is located in Canada or Puerto Rico, the only
 * international destinations Electric can currently arrange manual shipments to.
 *
 * @param site - The site to check
 * @returns true if the site's country is CA or PR, false otherwise
 */
export const isCaOrPrSite = (site: Site): boolean => {
  const country = site.address?.country?.toUpperCase();
  return !!country && CA_PR_COUNTRY_CODES.includes(country);
};

/**
 * Filters out international sites from a list of sites.
 * Useful for features that should only show US-based sites (e.g., offboarding, QR codes, shipping).
 *
 * @param sites - Array of sites to filter
 * @returns Array of sites excluding international sites
 */
export const filterOutInternationalSites = (sites: Site[]): Site[] => {
  return sites.filter(site => !isInternationalSite(site));
};
